import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useBuyerTracker, useAddBuyers } from "@/hooks/useBuyerTracker";

export interface SendCimQueueItem {
  linkId: string;
  buyerId: string;
  buyerName: string;
  ndaMissing: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  clientId: string;
  /** Hand the selected buyers off to the step-through send flow */
  onStart: (queue: SendCimQueueItem[]) => void;
}

interface BuyerLite { id: string; name: string; subType?: string | null; headquarters?: string | null }

export function SendCimPicker({ open, onClose, clientId, onStart }: Props) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Map<string, SendCimQueueItem>>(new Map());
  const [networkOpen, setNetworkOpen] = useState(false);
  const [networkSearch, setNetworkSearch] = useState("");
  const addBuyers = useAddBuyers(clientId);

  const { data, isLoading } = useBuyerTracker(clientId, {
    page: 1, pageSize: 200, sort: "created", order: "desc",
    search, status: "active", milestones: [],
  });

  const rows = data?.rows ?? [];
  const ready = rows.filter((r) => r.ndaExecutedAt && !r.cimSentAt);
  const noNda = rows.filter((r) => !r.ndaExecutedAt && !r.cimSentAt);
  const alreadySent = data?.aggregates.cims ?? 0;
  const truncated = (data?.totalRows ?? 0) > rows.length;

  // Buyer Network candidates for "add to tracker" (same source as AddBuyersDialog)
  const { data: networkBuyers = [] } = useQuery<BuyerLite[]>({
    queryKey: ["buyers-for-add"],
    queryFn: () =>
      apiRequest("GET", `/api/buyers?limit=all`)
        .then((r) => r.json())
        .then((d) => (Array.isArray(d) ? d : d.data ?? d.rows ?? d.buyers ?? [])),
    enabled: open && networkOpen,
    staleTime: 60_000,
  });
  const trackerBuyerIds = useMemo(() => new Set(rows.map((r) => r.buyerId)), [rows]);
  const networkFiltered = networkBuyers
    .filter((b) => !trackerBuyerIds.has(b.id) && b.name.toLowerCase().includes(networkSearch.toLowerCase()))
    .slice(0, 50);

  const toggle = (item: SendCimQueueItem) =>
    setSelected((p) => {
      const n = new Map(p);
      n.has(item.linkId) ? n.delete(item.linkId) : n.set(item.linkId, item);
      return n;
    });

  const addFromNetwork = (b: BuyerLite) => {
    addBuyers.mutate(
      { buyerIds: [b.id], markTeaserNow: true },
      {
        onSuccess: (res) => {
          const link = res.links?.[0];
          if (!link) { toast.error("Buyer added but link not returned — reopen this dialog"); return; }
          setSelected((p) => new Map(p).set(link.linkId, {
            linkId: link.linkId, buyerId: b.id, buyerName: b.name, ndaMissing: true,
          }));
          toast.success(`${b.name} added to the tracker and selected`);
          setNetworkSearch("");
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const buyerRow = (linkId: string, buyerId: string, name: string, sub: string, ndaMissing: boolean) => {
    const item: SendCimQueueItem = { linkId, buyerId, buyerName: name, ndaMissing };
    const checked = selected.has(linkId);
    return (
      <label key={linkId} className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm ${checked ? "bg-blue-50 dark:bg-blue-950/40" : "hover:bg-slate-50 dark:bg-slate-950/40"}`}>
        <input type="checkbox" checked={checked} onChange={() => toggle(item)} />
        <span className="flex-1 truncate font-medium text-slate-900 dark:text-slate-200">{name}</span>
        <span className="text-[11px] text-slate-400">{sub}</span>
      </label>
    );
  };

  const handleClose = () => { setSelected(new Map()); setSearch(""); setNetworkOpen(false); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send CIM — choose buyers</DialogTitle>
        </DialogHeader>

        <Input className="h-8" placeholder="Search buyers in market" value={search} onChange={(e) => setSearch(e.target.value)} />

        {isLoading ? (
          <div className="p-6 text-center text-sm text-slate-400">Loading…</div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                NDA signed, CIM not sent · {ready.length}
              </div>
              <div className="max-h-48 overflow-y-auto">
                {ready.map((r) => buyerRow(r.linkId, r.buyerId, r.buyerName, r.buyerFirmType ?? "", false))}
                {ready.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">None waiting.</div>}
              </div>
            </div>

            {noNda.length > 0 && (
              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  No NDA marked yet · {noNda.length}
                </div>
                <div className="max-h-36 overflow-y-auto">
                  {noNda.slice(0, 50).map((r) => buyerRow(r.linkId, r.buyerId, r.buyerName, r.buyerFirmType ?? "", true))}
                </div>
                <div className="mt-0.5 px-2 text-[11px] text-amber-600 dark:text-amber-400">
                  Selecting one of these lets you mark Teaser + NDA during the send.
                </div>
              </div>
            )}

            {(alreadySent > 0 || truncated) && (
              <div className="text-[11px] text-slate-400">
                {alreadySent > 0 && `${alreadySent} buyer${alreadySent === 1 ? " has" : "s have"} already received the CIM (not shown).`}
                {truncated && " Showing the first 200 — use search to find anyone else."}
              </div>
            )}

            <div className="rounded-lg border border-dashed border-slate-300 p-2.5 dark:border-slate-800">
              {!networkOpen ? (
                <button className="text-xs font-semibold text-blue-600 hover:underline dark:text-blue-400" onClick={() => setNetworkOpen(true)}>
                  + Add a buyer from the Buyer Network
                </button>
              ) : (
                <div className="space-y-1.5">
                  <Input
                    className="h-8"
                    placeholder="Search the Buyer Network"
                    value={networkSearch}
                    onChange={(e) => setNetworkSearch(e.target.value)}
                    autoFocus
                  />
                  <div className="max-h-36 overflow-y-auto">
                    {networkFiltered.map((b) => (
                      <button
                        key={b.id}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50 dark:bg-slate-950/40"
                        disabled={addBuyers.isPending}
                        onClick={() => addFromNetwork(b)}
                      >
                        <span className="flex-1 truncate font-medium">{b.name}</span>
                        <span className="text-[11px] text-slate-400">{[b.subType, b.headquarters].filter(Boolean).join(" · ")}</span>
                        <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400">add + select</span>
                      </button>
                    ))}
                    {networkSearch && networkFiltered.length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-slate-400">No matches outside the tracker.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={handleClose}>Cancel</Button>
          <Button size="sm" disabled={selected.size === 0} onClick={() => { onStart([...selected.values()]); handleClose(); }}>
            Continue{selected.size > 0 ? ` (${selected.size})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
